import { on } from '@ngrx/store';

import { LoadingState } from 'src/app/utilities/state.util';

import { BlueSageTerminalPickerActions } from './bluesage-terminal-picker.actions';
import {
    BlueSageTerminalPickerState,
    RECENT_TERMINALS_MAX,
} from './bluesage-terminal-picker.state';

export const blueSageTerminalPickerReducers = [

    on(
        BlueSageTerminalPickerActions.pickerOpened,
        BlueSageTerminalPickerActions.refreshButtonClicked,
        BlueSageTerminalPickerActions.terminalReleased,
        (state: BlueSageTerminalPickerState, action): BlueSageTerminalPickerState => ({
            ...state,
            // A newly opened picker starts collapsed. A refresh does not — the
            // operator was looking at the full list for a reason.
            expanded: action.type === BlueSageTerminalPickerActions.pickerOpened.type
                ? false
                : state.expanded,
            loading: LoadingState.LOADING,
            // Requirement: the flag describes the MOST RECENT attempt only. Leaving
            // a previous failure set would keep "could not load the terminals" on
            // screen through a request that is working fine — the same defect the
            // review queue shipped with and had to fix in review.
            loadFailed: false,
        }),
    ),

    on(
        BlueSageTerminalPickerActions.loadTerminalsSuccess,
        (state: BlueSageTerminalPickerState, { terminals }): BlueSageTerminalPickerState => ({
            ...state,
            terminals,
            loading: LoadingState.LOADED,
            loadFailed: false,
            // A device that vanished from the roster cannot stay selected — Send
            // would target an EPI this location no longer has. A roster of one
            // is preselected: there is no choice left for the operator to make.
            selectedEpi: terminals.some((t) => t.epi === state.selectedEpi)
                ? state.selectedEpi
                : terminals.length === 1 ? terminals[0].epi : undefined,
        }),
    ),

    on(
        BlueSageTerminalPickerActions.loadTerminalsError,
        (state: BlueSageTerminalPickerState): BlueSageTerminalPickerState => ({
            ...state,
            loading: LoadingState.LOADED,
            loadFailed: true,
        }),
    ),

    on(
        BlueSageTerminalPickerActions.searchChanged,
        (state: BlueSageTerminalPickerState, { search }): BlueSageTerminalPickerState => ({ ...state, search }),
    ),

    on(
        BlueSageTerminalPickerActions.terminalSelected,
        (state: BlueSageTerminalPickerState, { epi }): BlueSageTerminalPickerState => ({
            ...state,
            selectedEpi: epi,
            // Picking a device from the expanded list collapses it again: the
            // question has been answered, and leaving thirty rows open pushes the
            // Send button off a tablet screen.
            expanded: false,
        }),
    ),

    on(
        BlueSageTerminalPickerActions.showAllTerminalsButtonClicked,
        (state: BlueSageTerminalPickerState): BlueSageTerminalPickerState =>
            ({ ...state, expanded: true }),
    ),

    on(
        BlueSageTerminalPickerActions.showFewerTerminalsButtonClicked,
        (state: BlueSageTerminalPickerState): BlueSageTerminalPickerState =>
            ({ ...state, expanded: false }),
    ),

    on(
        BlueSageTerminalPickerActions.recentsLoaded,
        (state: BlueSageTerminalPickerState, { recentEpis }): BlueSageTerminalPickerState => ({ ...state, recentEpis }),
    ),

    on(
        BlueSageTerminalPickerActions.terminalUsed,
        (state: BlueSageTerminalPickerState, { epi }): BlueSageTerminalPickerState => ({
            ...state,
            // Most recent first, no duplicates, capped. The write to localStorage
            // is a side effect and lives in the effects file.
            recentEpis: [ epi, ...state.recentEpis.filter((e) => e !== epi) ]
                .slice(0, RECENT_TERMINALS_MAX),
        }),
    ),
];
